import { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api-client';
import { noticeParts } from '../../lib/notice-link';
import { formatMoney, poundsToPence } from '../../lib/money';
import type { QuotationPreviewLine } from './QuotationPreview';
import { DocLogo } from './DocLogo';

const DEFAULTS = {
  companyName: 'Maxiron Ltd',
  companyNumber: '14444817',
  vatNumber: '435737379',
  companyAddress: '3rd Floor 86-90 Paul Street, London, EC2A 4NE',
  officeEmail: 'office@maxiron.com',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function longDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return iso;
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1] || ''} ${match[1]}`.trim();
}

function RichText({ value }: { value: string }) {
  const lines = value.replace(/\t/g, ' ').split('\n');
  return (
    <span className="block">
      {lines.map((line, index) => (
        <span key={index} className="block" style={{ marginTop: index ? 6 : 0 }}>
          {line.trim()
            ? noticeParts(line).map((part, partIndex) => part.href
              ? <a key={partIndex} href={part.href} className="underline" style={{ color: '#1155cc' }} target="_blank" rel="noreferrer">{part.text}</a>
              : <span key={partIndex}>{part.text}</span>)
            : '\u00a0'}
        </span>
      ))}
    </span>
  );
}

export function QuotationFormat2({
  number,
  customerName,
  title,
  issuedOn,
  currency,
  validDays,
  billToName,
  billToAddress,
  billToEmail,
  contactName,
  contactRole,
  contactEmail,
  contactPhone,
  notice,
  scope,
  paymentTerms,
  delivery,
  acceptance,
  lines,
  vatPence,
}: {
  number?: string;
  customerName: string;
  title: string;
  issuedOn: string;
  currency?: string;
  validDays: number;
  billToName: string;
  billToAddress: string;
  billToEmail: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  notice: string;
  scope: string;
  paymentTerms: string;
  delivery: string;
  acceptance: string;
  lines: QuotationPreviewLine[];
  vatPence?: number | null;
}) {
  const [company, setCompany] = useState(DEFAULTS);
  useEffect(() => {
    void apiGet<{ settings?: Record<string, string> }>('/api/settings').then((data) => {
      const s = data.settings || {};
      setCompany({
        companyName: s.companyName || DEFAULTS.companyName,
        companyNumber: s.companyNumber || DEFAULTS.companyNumber,
        vatNumber: s.vatNumber || DEFAULTS.vatNumber,
        companyAddress: s.companyAddress || DEFAULTS.companyAddress,
        officeEmail: s.billingEmail || DEFAULTS.officeEmail,
      });
    }).catch(() => setCompany(DEFAULTS));
  }, []);

  const money = currency || 'GBP';
  const shown = lines.filter((line) => line.description || line.pounds);
  const net = shown.reduce((sum, line) => sum + Math.round((Number(line.qty) || 1) * poundsToPence(line.pounds)), 0);
  const vatKnown = vatPence != null;
  const vat = vatPence ?? 0;
  const total = vatKnown ? net + vat : net;
  const address = billToAddress.split(/\n|,/).map((part) => part.trim()).filter(Boolean);
  const prepared = billToName || customerName;
  const ref = number || 'Draft';
  const terms: [string, string][] = [
    ['Quotation validity', validDays > 0 ? `${validDays} days` : ''],
    ['Payment terms', paymentTerms],
    ['Delivery', delivery],
    ['Scope & exclusions', scope],
    ['Terms & conditions', notice],
  ].filter((row): row is [string, string] => Boolean(row[1].trim()));

  return (
    <article className="doc-q2">
      <header className="flex items-start justify-between gap-6">
        <DocLogo />
        <div className="text-right">
          <h1 className="text-[18pt] font-bold tracking-wide">QUOTATION</h1>
          <p className="mt-1 text-[9pt] leading-relaxed" style={{ color: '#655e70' }}>Reference <b className="font-bold" style={{ color: '#27212e' }}>{ref}</b></p>
          <p className="text-[9pt]" style={{ color: '#655e70' }}>Date <b className="font-bold" style={{ color: '#27212e' }}>{longDate(issuedOn)}</b></p>
        </div>
      </header>
      <p className="mt-5 text-[9pt] leading-relaxed" style={{ color: '#655e70' }}>
        <strong className="block text-[11pt] font-bold" style={{ color: '#24212d' }}>{company.companyName}</strong>
        {company.companyAddress}<br />
        Company No. {company.companyNumber} · VAT No. {company.vatNumber}<br />
        <a className="underline" href={`mailto:${company.officeEmail}`}>{company.officeEmail}</a>
      </p>
      <section className="mt-5 grid grid-cols-2 gap-6 border-t pt-4" style={{ borderColor: '#e9e4ee' }}>
        <div>
          <p className="eyebrow mb-2">Prepared for</p>
          <p className="text-[10pt] leading-relaxed">
            {prepared ? <strong className="font-bold">{prepared}</strong> : null}
            {prepared ? <br /> : null}
            {address.map((line) => <span key={line}>{line}<br /></span>)}
            {billToEmail ? <a className="underline" href={`mailto:${billToEmail}`}>{billToEmail}</a> : null}
          </p>
        </div>
        <div>
          <p className="eyebrow mb-2">Project</p>
          <h2 className="text-[14pt] font-bold leading-snug">{title || 'Quotation'}</h2>
        </div>
      </section>
      <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 rounded px-3 py-2 text-[9pt]" style={{ background: '#f6f3fa' }}>
        {contactName ? <strong className="font-bold">{contactName}</strong> : null}
        {contactRole ? <span style={{ color: '#76687f' }}>{contactRole}</span> : null}
        {contactEmail ? <a className="underline" href={`mailto:${contactEmail}`}>{contactEmail}</a> : null}
        {contactPhone ? <span>{contactPhone}</span> : null}
      </p>
      <table className="mt-4">
        <thead>
          <tr>
            <th className="qty">Qty</th>
            <th>Description</th>
            <th className="num">Unit price</th>
            <th className="num">Line total</th>
          </tr>
        </thead>
        <tbody>
          {(shown.length ? shown : [{ qty: '', description: '', pounds: '' }]).map((line, index) => {
            const qty = Number(line.qty) || (line.pounds || line.description ? 1 : 0);
            const unit = poundsToPence(line.pounds);
            return (
              <tr key={index}>
                <td className="qty">{qty || ''}</td>
                <td className="whitespace-pre-line font-bold">{line.description}</td>
                <td className="num">{line.pounds ? formatMoney(unit, money) : ''}</td>
                <td className="num">{line.pounds ? formatMoney(Math.round(qty * unit), money) : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 ml-auto w-56 text-[10pt]">
        <div className="flex justify-between px-2 py-1"><span>Subtotal</span><span>{formatMoney(net, money)}</span></div>
        <div className="flex justify-between px-2 py-1"><span>VAT</span><span className={vatKnown ? '' : 'font-bold'}>{vatKnown ? formatMoney(vat, money) : 'NONE'}</span></div>
        <div className="mt-1 flex justify-between px-2 py-2 text-[12pt] font-bold" style={{ background: '#f6f0fb', color: '#522479', borderTop: '1px solid #d9c6e9' }}>
          <span>Total {money}</span><span>{formatMoney(total, money)}</span>
        </div>
      </div>
      {terms.length ? (
        <section className="mt-6 border-t pt-4" style={{ borderColor: '#ded5e6' }}>
          <h2 className="mb-3 text-[12pt] font-bold">Commercial terms</h2>
          <dl className="grid grid-cols-[7.2rem_1fr] gap-x-4 gap-y-4 text-[9pt] leading-relaxed">
            {terms.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="font-bold">{label}</dt>
                <dd style={{ color: '#554d61' }}><RichText value={value} /></dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      <section className="mt-5 border-l-[3px] pl-3 text-[9pt] leading-relaxed" style={{ borderColor: '#9b5bc9' }}>
        <strong className="mb-1 block font-bold">To proceed</strong>
        {acceptance.trim() ? <p style={{ color: '#554d61' }}><RichText value={acceptance} /></p> : null}
        <p style={{ color: '#554d61' }}>Please quote reference <b className="font-bold">{ref}</b> in your response or purchase order.</p>
      </section>
      <footer className="mt-auto pt-6">
        <p className="mb-4 text-center text-[10pt]" style={{ color: '#6b577b' }}>Thank you for considering MaxIron.</p>
        <div className="flex justify-between gap-3 border-t pt-2 text-[8pt]" style={{ borderColor: '#e9e4ee', color: '#847b8d' }}>
          <span>{company.companyName} · maxiron.com · {company.officeEmail}</span>
          <span>{ref} · Page 1 of 1</span>
        </div>
      </footer>
    </article>
  );
}
